import { Box, CircularProgress } from "@mui/material";
import React, { useState } from "react";
import { CachePolicies } from "use-http";

import { UserLimitModal } from "components/account/UserLimitModal/UserLimitModal";
import { CustomSwitch } from "components/common/CustomSwitch";
import { JANZU_URL } from "config/config";
import { useAppDispatch } from "hooks/store";
import { useFetchCustom } from "hooks/useFetchCustom";
import { useModalLimit } from "hooks/useModalLimit/useModalLimit";
import { ConfirmDeleteModal } from "new-components/modals/ConfirmDeleteModal";
import { Typography } from "new-components/typography/Typography";
import { getDataLimit } from "store/slices/DataLimit/dataLimitSlice";
import { getUserLimit } from "store/slices/UserLimit/userLimitSlice";
import { Detector } from "types/detectors";


interface DetectorControlsProps {
  acceptingNewQueriesValue: "ON" | "OFF";
  detectorId: string;
  getDetector?: () => Promise<Detector>;
}

export function DetectorEnabledSwitch(props: DetectorControlsProps) {
  const { acceptingNewQueriesValue, detectorId, getDetector } = props;

  const { isBlocked, modalLimit, handleOpenModalLimit, handleCloseModalLimit } = useModalLimit("detectorIsBlocked");

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [statusValue, setStatusValue] = useState<"ON" | "OFF">(acceptingNewQueriesValue);

  const oldUrl = `${JANZU_URL}device-api/predictors/${detectorId}`;
  const { patch: updateDetector, response } = useFetchCustom<Detector>(oldUrl, {
    cachePolicy: CachePolicies.NO_CACHE,
  });

  const dispatch = useAppDispatch();

  const handleChangeStatus = async (value: "ON" | "OFF") => {
    try {
      handleIsLoading(true);
      await updateDetector({
        status: value,
      });
      const resStatus = await response.status;
      if (resStatus !== 200) {
        setStatusValue(value === "ON" ? "OFF" : "ON");
      } else {
        setStatusValue(value);
        if (getDetector) getDetector();
      }
      await dispatch(getDataLimit());
      await dispatch(getUserLimit());
    } catch (error) {
      console.error(error);
    } finally {
      handleIsLoading(false);
    }
  };

  const handleConfirm = () => {
    handleChangeStatus("OFF");
    setOpenModal(false);
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleIsLoading = (value: boolean) => {
    setIsLoading(value);
  };

  const handlClickChangeValue = (e: React.MouseEvent) => {
    e.stopPropagation();
    const value = statusValue === "ON" ? "OFF" : "ON";
    if (value === "ON") {
      if (isBlocked) {
        handleOpenModalLimit();
        return;
      }

      handleChangeStatus(value);
      return;
    }
    handleOpenModal();
  };

  const handleCloseLimit = () => {
    handleCloseModalLimit();
  };

  // Probably we can move this stopPropagation to the CustomModal component
  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleModalKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.stopPropagation();
    }
  };

  return (
    <>
      <Box className="flex gap-[10px] items-center" onClick={handlClickChangeValue}>
        <Typography variant="detail" fontWeight="semibold" textTransform="uppercase" className="hidden md:block" noWrap>
          Detector Enabled:
        </Typography>
        {isLoading ? (
          <CircularProgress size={22} sx={{ color: "#0450BA", marginRight: "16px" }} />
        ) : (
          <CustomSwitch
            checked={statusValue === "ON"}
            onClick={handlClickChangeValue}
            inputProps={{ "aria-label": "controlled" }}
            disabled={isLoading}
          />
        )}
      </Box>
      <Box
        onClick={handleModalClick}
        onKeyDown={handleModalKeyDown}
        role="presentation"
      >
        <UserLimitModal type="detector" isModalOpen={modalLimit} handleCloseModal={handleCloseLimit} />
        <ConfirmDeleteModal
          isDeleteModalOpen={openModal}
          setIsDeleteModalOpen={setOpenModal}
          modalTitle="Accepting New Queries"
          modalContent="When disabled, no new queries will be accepted by the detector."
          onRemoveHandler={handleConfirm}
          buttonTexts={{
            cancelText: "Cancel",
            successText: "Ok",
          }}
        />
      </Box>
    </>
  );
}
